import React, { useState } from 'react';

import { css } from '../Viewer/lut';
import { COMPOSITE_BLACKS, COMPOSITE_COLORS, COMPOSITE_GAINS, COMPOSITE_KEYS } from '../Viewer/composite';
import { CardTitle, Slider } from './ChannelCard';

const num = (v, digits = 2) => v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const NAMES = { r: 'Red', g: 'Green', b: 'Blue' };

// Canal « Composite RGB » (src/components/Viewer/composite.js), présenté comme une modalité : interrupteur (exclusif),
// image de chaque couleur (choisir une image déjà prise par une autre couleur les échange), rotation des couleurs,
// fond mis à 0 et intensité de chaque couleur, équilibrage automatique (onBalance), affichage direct ou différences
// accentuées (décorrélation, force) ; stats : statistiques du tissu (variances des composantes). chans : images résolues
// [rouge, vert, bleu] ; blacks : niveaux mis à 0 appliqués (réglés ou fonds estimés).
export default function CompositeCard({
  channels, chans, settings, stats, blacks, active, error, onChange, onToggle, onBalance, onRecipe, collapsed, onCollapse,
  label, onRename,
}) {
  const [balancing, setBalancing] = useState(false);
  const balance = async () => {
    setBalancing(true);
    try { await onBalance(); } finally { setBalancing(false); }
  };
  const ids = Object.fromEntries(COMPOSITE_KEYS.map((key, i) => [key, chans[i]?.id ?? null]));
  const gain = key => settings[COMPOSITE_GAINS[key]] || 1;
  const black = key => settings[COMPOSITE_BLACKS[key]] ?? null;
  // Réglages (intensité, fond) d'une couleur repris par une autre : ils suivent leur image.
  const levelsOf = (to, from) => ({ [COMPOSITE_GAINS[to]]: gain(from), [COMPOSITE_BLACKS[to]]: black(from) });
  // Affectation d'une image à une couleur : si une autre couleur l'avait, elle prend l'ancienne image de celle-ci.
  const assign = (key, id) => {
    const other = COMPOSITE_KEYS.find(k => k !== key && ids[k] === id);
    onChange({ ...ids, [key]: id, ...(other ? { [other]: ids[key], ...levelsOf(key, other), ...levelsOf(other, key) } : {}) });
  };
  // Rotation rouge -> vert -> bleu -> rouge, réglages compris.
  const rotate = () => onChange({
    r: ids.b, g: ids.r, b: ids.g, ...levelsOf('r', 'b'), ...levelsOf('g', 'r'), ...levelsOf('b', 'g'),
  });
  // Fond affiché en unités d'origine si connues ; « auto » : estimé sur l'image.
  const blackLabel = (ch, key, v) => `${Math.round(ch?.toOriginal ? ch.toOriginal(v) : v)}${black(key) == null ? ' (auto)' : ''}`;
  return (
    <div className={`channel derived${active ? '' : ' off'}${collapsed ? ' collapsed' : ''}`}>
      <div className="channel-header">
        <span className="swatch composite" title="Computed channel: RGB composite" />
        <CardTitle name={label || 'RGB composite'} collapsed={collapsed} onCollapse={onCollapse} onRename={onRename} />
        <label className="switch" title="Show (hides the other modalities)">
          <input type="checkbox" checked={active} onChange={ev => onToggle(ev.target.checked)} />
          <span />
        </label>
      </div>
      {!collapsed && (
        <div className="channel-body compare">
          <p className="viewer-note" style={{ margin: 0 }}>
            Three fluorescence images in red, green and blue, each with the contrast and blur of its card.
          </p>
          {error && <p className="viewer-note error">{error}</p>}
          {COMPOSITE_KEYS.map(key => (
            <div key={key} className="compare-row">
              <span className="compare-key" style={{ background: css(COMPOSITE_COLORS[key]) }}>{key.toUpperCase()}</span>
              <select value={ids[key] || ''} aria-label={`${NAMES[key]} image`}
                onChange={ev => assign(key, ev.target.value)}>
                {channels.map(ch => <option key={ch.id} value={ch.id}>{ch.name}</option>)}
              </select>
            </div>
          ))}
          <div className="channel-options">
            <button type="button" className="chip" onClick={rotate} title="Rotate the colors: red -> green -> blue -> red">
              Rotate
            </button>
            <button type="button" className="chip" onClick={balance} disabled={balancing || !!error}
              title="Automatically balance the intensity of each color: the 99.5th percentile of its image at the top of its contrast">
              {balancing ? 'Computing…' : 'Balance'}
            </button>
            <button type="button" className="chip" onClick={() => onChange({ blackR: null, blackG: null, blackB: null })}
              title="Automatic backgrounds: level set to 0 for each color = background estimated on its image (median below the Otsu threshold)">
              Auto backgrounds
            </button>
            <button type="button" className="chip" onClick={onRecipe} disabled={!!error || !chans.every(Boolean)}
              title="Download the recipe of this rendering (JSON: images, windows, settings, decorrelation matrix) for tools/composite/composite_render.py">
              Recipe
            </button>
          </div>
          <div className="compare-row">
            <span className="compare-label">Display</span>
            <select value={settings.display || 'direct'} aria-label="Composite display"
              onChange={ev => onChange({ display: ev.target.value })}>
              <option value="direct">Direct (intensities)</option>
              <option value="decorrelated">Enhanced differences (decorrelation)</option>
            </select>
          </div>
          {settings.display === 'decorrelated' && (
            <>
              <p className="viewer-note" style={{ margin: 0 }}>
                Removes what the three channels have in common and equalizes what sets them apart: each difference in
                composition becomes a color (red stays the red channel). Mid gray = average tissue.
                {stats ? ` Variance shared by the three channels: ${Math.round((100 * Math.max(...stats.variances)) / stats.variances.reduce((a, v) => a + v, 0))}%; tissue: ${Math.round(stats.area * 100)}% of the image.` : ' Computing…'}
              </p>
              <Slider label={`Background threshold (sum of the three intensities; below: black)${settings.threshold == null ? ' · Otsu' : ''}`}
                value={settings.threshold ?? stats?.otsu ?? 0} max={3} step={0.01} reset={null}
                format={v => num(v)} onChange={v => onChange({ threshold: v })} />
              <Slider label="Decorrelation strength" value={settings.strength ?? 1} max={1} step={0.05} reset={1}
                onChange={v => onChange({ strength: v })} />
              <Slider label="Contrast (color spread around mid gray)" value={settings.contrast ?? 0.1}
                min={0.03} max={0.3} step={0.005} reset={0.1} format={v => num(v, 3)}
                onChange={v => onChange({ contrast: v })} />
            </>
          )}
          <Slider label="Color saturation" value={settings.saturation ?? 1} max={3} step={0.05} reset={1}
            format={v => `× ${num(v)}`} onChange={v => onChange({ saturation: v })} />
          <Slider label="Image black point (below: black; the LUT starts from there)" value={settings.black ?? 0} max={0.9}
            step={0.01} reset={0} onChange={v => onChange({ black: v })} />
          <Slider label="Smoothing (against color noise, σ in screen px)" value={settings.smooth ?? 1} max={4} step={0.5}
            reset={1} format={v => (v ? `σ ${num(v, 1)} px` : 'none')} onChange={v => onChange({ smooth: v })} />
          {COMPOSITE_KEYS.map((key, i) => (
            <React.Fragment key={key}>
              <Slider label={`${NAMES[key]} background set to 0${chans[i] ? ` (${chans[i].name})` : ''}`}
                value={blacks[i] ?? 0} max={chans[i]?.contrast[1] ?? 255} step={(chans[i]?.contrast[1] ?? 255) / 500}
                reset={null} format={v => blackLabel(chans[i], key, v)}
                onChange={v => onChange({ [COMPOSITE_BLACKS[key]]: v })} />
              <Slider label={`${NAMES[key]} intensity${chans[i] ? ` (${chans[i].name})` : ''}`}
                value={Math.log2(gain(key))} min={-2} max={2} step={0.05} reset={0}
                format={v => `× ${num(2 ** v)}`}
                onChange={v => onChange({ [COMPOSITE_GAINS[key]]: Math.round(2 ** v * 100) / 100 })} />
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
